/**
 * Structured error thrown when JSON parsing fails on a fetch Response.
 * Includes response metadata for observability without leaking body content.
 */
export class JsonParseError extends Error {
  override name = "JsonParseError" as const;
  constructor(
    message: string,
    public readonly responseStatus: number,
    public readonly responseUrl: string
  ) {
    super(message);
  }
}

/**
 * Safely extracts and parses JSON from a Response object.
 * Reads the body as text first and uses JSON.parse in a try/catch,
 * preventing the browser's native SyntaxError from propagating when
 * the body is empty or truncated.
 *
 * Throws `JsonParseError` with contextual metadata if parsing fails.
 */
export async function safeResponseJson<T>(response: Response): Promise<T> {
  const text = await response.text();

  if (!text || !text.trim()) {
    throw new JsonParseError(
      `Empty response body (status ${response.status})`,
      response.status,
      response.url
    );
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new JsonParseError(
      `Malformed JSON response (status ${response.status})`,
      response.status,
      response.url
    );
  }
}

/**
 * Checks if content-type header indicates JSON (case-insensitive)
 */
function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType.toLowerCase().includes("application/json");
}

/**
 * Handles API response errors and JSON parsing with proper error handling.
 * Reads the response body as text first to avoid stream consumption issues.
 * Error messages are sanitized to prevent sensitive information leakage.
 *
 * For success-path JSON parsing, delegates to `safeResponseJson` to avoid
 * duplicating the text-first safe-parse pattern.
 *
 * @param response - The fetch Response object
 * @returns The parsed JSON response as type T
 * @throws Error with descriptive message if response is not OK or JSON parsing fails
 */
export async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      const body = await response.text();
      if (isJsonContentType(contentType)) {
        const errorData = JSON.parse(body);
        const safeMessage = errorData.message || errorData.error;
        if (safeMessage && typeof safeMessage === "string") {
          errorMessage = safeMessage;
        }
      } else {
        errorMessage = response.statusText || errorMessage;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  const contentType = response.headers.get("content-type");
  if (!isJsonContentType(contentType)) {
    await response.text();
    throw new Error(
      `Expected JSON response but received ${contentType || "unknown content type"}`
    );
  }

  try {
    return await safeResponseJson<T>(response);
  } catch (error) {
    if (error instanceof JsonParseError) {
      throw new Error(`Failed to parse JSON response: ${error.message}`);
    }
    throw error;
  }
}
